import { useEffect, useMemo, useState } from "react";

import { IUsageEnforcementConfig } from "features/usage-config/types/usage-enforcement.model";

import {
  draftFromConfig,
  OverridableField,
  restoreField,
  sameFields,
  UsageDraft,
} from "./usage-config-draft";

export const useUsageConfigDraft = (config: IUsageEnforcementConfig | undefined) => {
  const [draft, setDraft] = useState<UsageDraft | null>(null);
  const [overrides, setOverrides] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!config) {
      setDraft(null);
      setOverrides(new Set());
      return;
    }
    setDraft(draftFromConfig(config));
    setOverrides(new Set(config.overridden_fields ?? []));
  }, [config]);

  const touch = (field: OverridableField) => {
    setOverrides((current) => {
      if (current.has(field)) return current;
      const next = new Set(current);
      next.add(field);
      return next;
    });
  };

  const follow = (field: OverridableField, source: IUsageEnforcementConfig) => {
    setOverrides((current) => {
      if (!current.has(field)) return current;
      const next = new Set(current);
      next.delete(field);
      return next;
    });
    setDraft((current) =>
      current ? restoreField(current, draftFromConfig(source), field) : current,
    );
  };

  const discard = () => {
    if (!config) return;
    setDraft(draftFromConfig(config));
    setOverrides(new Set(config.overridden_fields ?? []));
  };

  const isDirty = useMemo(() => {
    if (!config || !draft) return false;
    return (
      JSON.stringify(draft) !== JSON.stringify(draftFromConfig(config)) ||
      !sameFields(overrides, config.overridden_fields ?? [])
    );
  }, [config, draft, overrides]);

  return { draft, setDraft, overrides, touch, follow, discard, isDirty };
};
